import { useEffect, useState } from 'react';

/**
 * Custom hook to detect horizontal overflow in a container element
 * @param containerRef - React ref to the container element to observe
 * @param dependencies - Array of dependencies that should trigger overflow recalculation
 * @returns boolean indicating whether the container has horizontal overflow
 */
export const useHorizontalOverflow = <T extends HTMLElement>(
  containerRef: React.RefObject<T | null>,
  dependencies: React.DependencyList = [],
) => {
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const table = container.querySelector('table');
      const hasOverflow = table
        ? table.scrollWidth > container.clientWidth
        : container.scrollWidth > container.clientWidth;

      setHasHorizontalOverflow(hasOverflow);
    };

    const observer = new ResizeObserver(checkOverflow);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    window.addEventListener('resize', checkOverflow);

    // Initial check
    checkOverflow();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dependencies is a user-provided dep list that must be spread
  }, [containerRef, ...dependencies]);

  return hasHorizontalOverflow;
};
